import { useState, type FormEvent } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { isStaleWrite, type ConsultantUserDto } from '@signalvote/shared';
import { useAuth } from '@/contexts/AuthContext';
import {
  useConsultants,
  useConsultantUserSearch,
  useSetConsultant,
  useSetExpressLinkCode,
} from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const CONFLICT_MESSAGE = 'Someone else changed this user. Reloaded the latest; try again.';

const fullName = (u: ConsultantUserDto) => `${u.firstName} ${u.lastName}`.trim() || u.email;

const linkFor = (code: string) => `${window.location.origin}/express/${code}`;

// A hint only: the server owns the rule and also refuses reserved words and endings another link has.
const ENDING_FORMAT = /^(?=.{3,40}$)[a-z0-9]+(-[a-z0-9]+)*$/;
const ENDING_HINT = '3 to 40 lower-case letters, digits and single hyphens, with no hyphen at the start or end.';

/** Platform admins mark users as Consultants and see which campaigns each Consultant's Express link created. */
export function ConsultantsCard() {
  const { user } = useAuth();
  if (!user?.isPlatformAdmin) return null;
  return <ConsultantsEditor />;
}

function ConsultantsEditor() {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const consultants = useConsultants();
  const found = useConsultantUserSearch(query);
  const { mutate: setConsultant, isPending, variables } = useSetConsultant();

  const search = (e: FormEvent) => {
    e.preventDefault();
    setQuery(draft.trim());
  };

  const toggle = (u: ConsultantUserDto) => {
    setError(null);
    setConsultant(
      { userId: u.userId, request: { isConsultant: !u.isConsultant, concurrencyStamp: u.concurrencyStamp } },
      {
        onError: (e) =>
          setError(isStaleWrite(e) ? CONFLICT_MESSAGE : e instanceof Error ? e.message : "Couldn't save. Try again."),
      },
    );
  };

  const toggleButton = (u: ConsultantUserDto) => {
    const saving = isPending && variables?.userId === u.userId;
    return (
      <Button variant={u.isConsultant ? 'outline' : 'default'} size="sm" onClick={() => toggle(u)} disabled={isPending}>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        {u.isConsultant ? 'Remove Consultant' : 'Make Consultant'}
      </Button>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Consultants</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          A Consultant gets an Express link that sets up a new campaign and its first text. Removing one stops their link.
        </p>

        <form onSubmit={search} className="space-y-1.5">
          <Label htmlFor="consultant-search">Find a user</Label>
          <div className="flex gap-2">
            <Input
              id="consultant-search"
              placeholder="Name or email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button type="submit" variant="outline" disabled={draft.trim() === ''}>
              Search
            </Button>
          </div>
        </form>

        {query !== '' && (
          <div className="space-y-2">
            {found.isLoading ? (
              <p className="text-sm text-muted-foreground">Searching…</p>
            ) : found.data && found.data.items.length > 0 ? (
              <ul aria-label="Search results" className="divide-y rounded-md border">
                {found.data.items.map((u) => (
                  <li key={u.userId} aria-label={fullName(u)} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{fullName(u)}</p>
                      <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    {toggleButton(u)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No users match “{query}”.</p>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium">Consultants and their campaigns</p>
          {consultants.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : consultants.data && consultants.data.length > 0 ? (
            <ul aria-label="Consultants" className="divide-y rounded-md border">
              {consultants.data.map(({ user: u, campaigns }) => (
                <li key={u.userId} aria-label={fullName(u)} className="space-y-2 px-3 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {fullName(u)}
                        {!u.isConsultant && <span className="ml-2 text-xs font-normal text-muted-foreground">(no longer a Consultant)</span>}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    {toggleButton(u)}
                  </div>
                  {u.isConsultant && u.expressLinkCode && <ConsultantLink user={u} code={u.expressLinkCode} />}
                  {campaigns.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No campaigns from their link yet.</p>
                  ) : (
                    <ul className="space-y-1 pl-3">
                      {campaigns.map((c) => (
                        <li key={c.campaignId} className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{c.name}</span> · {c.ownerName} ({c.ownerEmail}) ·{' '}
                          {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No Consultants yet.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** A Consultant's Express link with a copy button, and an editor that replaces its ending. */
function ConsultantLink({ user, code }: { user: ConsultantUserDto; code: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(code);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: setLinkCode, isPending } = useSetExpressLinkCode();
  const ending = draft.trim().toLowerCase();
  const wellFormed = ENDING_FORMAT.test(ending);

  const copy = async () => {
    await navigator.clipboard.writeText(linkFor(code));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const open = () => {
    setDraft(code);
    setError(null);
    setEditing(true);
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLinkCode(
      { userId: user.userId, request: { code: ending, concurrencyStamp: user.concurrencyStamp } },
      {
        onSuccess: () => setEditing(false),
        onError: (e) =>
          setError(isStaleWrite(e) ? CONFLICT_MESSAGE : e instanceof Error ? e.message : "Couldn't save. Try again."),
      },
    );
  };

  if (!editing)
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="min-w-0 flex-1 break-all rounded-md border bg-muted/50 px-2 py-1 text-xs">{linkFor(code)}</code>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button variant="outline" size="sm" onClick={open}>
            Edit ending
          </Button>
        </div>
      </div>
    );

  const inputId = `express-ending-${user.userId}`;
  return (
    <form onSubmit={save} className="space-y-1.5 rounded-md border p-3">
      <Label htmlFor={inputId}>Link ending</Label>
      <Input
        id={inputId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-invalid={!wellFormed}
        autoComplete="off"
        spellCheck={false}
      />
      <p className={`text-xs ${wellFormed ? 'text-muted-foreground' : 'text-destructive'}`}>{ENDING_HINT}</p>
      {wellFormed && <p className="break-all text-xs text-muted-foreground">{linkFor(ending)}</p>}
      <p className="text-xs text-warning">
        The old link stops working as soon as you save. Anyone who has it will be told the link doesn’t exist.
      </p>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!wellFormed || ending === code || isPending}>
          {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          Save
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
